import { Button } from "./ui/button";
import { PageLink } from "./PageLink";
import { PageHero } from "./PageHero";
import { IndustryGrid } from "./ProductVisuals";
import { Card, CardContent } from "./ui/card";
import { Cpu, GraduationCap, FlaskConical, ArrowRight, Coins } from "lucide-react";
import { accelerators } from "../data/accelerators";
import { industries, chipClass } from "../data/solutions";
import { Mark } from './Mark';

interface SolutionsPageProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

// One line per industry for the hero grid.
const industryLines: Record<string, string> = {
  healthcare: "Chart audit, intake, notes",
  bfsi: "Loans, KYC, credit memos",
  insurance: "Claims, underwriting, complaints",
  "hr-tech": "Hiring, onboarding, Ask HR",
  legal: "Petition replies, NDAs",
  telecom: "SMS template compliance",
  manufacturing: "Shift logs, rejects, training",
  education: "Admissions you can explain",
  "invoice-processing": "Accounts payable",
  "it-ops": "Incidents, RCA, remediation",
};

// Accelerators & Tools — ready-to-run applications built on TurfAI.
const acceleratorIcons: Record<string, typeof Cpu> = { stageone: Cpu, learning: GraduationCap, promptbench: FlaskConical };

// Packs in design (ariviti.com/platform/domain-packs) — clearly labelled, non-clickable.
const comingSoon = [
  { icon: Coins, name: "FinTech", desc: "Onboarding, KYC packs, and statement processing." },
];

export function SolutionsPage({ onNavigate, onNavigateToDemo }: SolutionsPageProps) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title={<>Built for the work your industry <Mark>actually does</Mark>.</>}
        lede="Every engagement starts with a specific operational problem in a specific industry — domain-tuned workflows on the TurfAI platform, not a generic framework applied from the outside."
        actions={<Button variant="cta" size="cta" onClick={onNavigateToDemo}>Schedule a demo</Button>}
        visual={<IndustryGrid items={industries.map((i) => ({ name: i.name, line: industryLines[i.slug] ?? i.eyebrow }))} />}
      />


      {/* By industry */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">Where we operate</p>
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Solutions by <Mark>industry</Mark></h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              Domain-tuned workflows mapped to TurfAI Domain Packs.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {industries.map((ind) => (
              <PageLink page={ind.pageId} onNavigate={onNavigate}
                key={ind.slug}
                className="text-left p-6 rounded-2xl border border-softblack-tint-20 hover:border-indigo-tint-50 transition-all"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${chipClass(ind.accent)}`}>
                  <ind.icon className="w-6 h-6" />
                </div>
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-1">{ind.name}</h3>
                <p className="text-soft-black mb-3">{ind.headline}</p>
                <p className="text-sm font-medium text-soft-black mb-3">{ind.proof[0].metric} {ind.proof[0].label}</p>
                <span className="text-sm font-medium text-royal-indigo inline-flex items-center">
                  Explore {ind.name} <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </PageLink>
            ))}
            {comingSoon.map((c) => (
              <div key={c.name} className="p-6 rounded-2xl border border-dashed border-indigo-tint-50 bg-white">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-softblack-tint-20 text-soft-black">
                  <c.icon className="w-6 h-6" />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-[22px] lg:text-[24px] font-semibold text-soft-black">{c.name}</h3>
                  <span className="text-xs font-medium text-soft-black bg-softblack-tint-20 rounded-full px-2 py-0.5">In design</span>
                </div>
                <p className="text-soft-black">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accelerators (packaged solutions) */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4"><Mark>Accelerators &amp; Tools</Mark></h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              Ready-to-run applications built on TurfAI. Run them as they are or adapt them.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {accelerators.map((a) => ({ id: a.pageId, icon: acceleratorIcons[a.pageId] ?? Cpu, name: a.name, tagline: a.menuDesc, desc: a.subhead })).map((p) => (
              <Card key={p.id} className="p-6 bg-white border-softblack-tint-20 transition-shadow">
                <CardContent className="p-0">
                  <div className="w-14 h-14 bg-indigo-tint-20 rounded-2xl flex items-center justify-center mb-5">
                    <p.icon className="w-7 h-7 text-royal-indigo" />
                  </div>
                  <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-1">{p.name}</h3>
                  <p className="text-sm font-medium text-royal-indigo mb-3">{p.tagline}</p>
                  <p className="text-sm text-soft-black mb-5">{p.desc}</p>
                  <Button variant="outline" className="border-indigo-tint-50 text-soft-black hover:bg-softblack-tint-20" asChild><PageLink page={p.id} onNavigate={onNavigate}>
                    Explore {p.name} <ArrowRight className="w-4 h-4 ml-2" />
                  </PageLink></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Example workflows */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Example <Mark>workflows</Mark></h2>
            <p className="text-[22px] text-soft-black max-w-2xl mx-auto">
              Patterns teams ship on day one — fully editable on the canvas.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Card className="p-6 bg-indigo-tint-05 border-softblack-tint-20">
              <CardContent className="p-0">
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-2">KYC and Aadhaar extraction</h3>
                <p className="text-sm text-soft-black">
                  Upload an ID document → extract the structured details → email the verified results.
                </p>
              </CardContent>
            </Card>
            <Card className="p-6 bg-indigo-tint-05 border-softblack-tint-20">
              <CardContent className="p-0">
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-2">Document type routing</h3>
                <p className="text-sm text-soft-black">
                  Upload a document → classify it → route to the right extractor → notify stakeholders.
                </p>
              </CardContent>
            </Card>
            <Card className="p-6 bg-indigo-tint-05 border-softblack-tint-20">
              <CardContent className="p-0">
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-2">Job application screening</h3>
                <p className="text-sm text-soft-black">
                  Resume upload → extract fields → match against the job description → email HR and the hiring manager.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight mb-6">Have a <Mark>workflow</Mark> in mind?</h2>
          <p className="text-[24px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">
            Tell us what you want to automate — we'll show you how it runs on TurfAI.
          </p>
          <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>
            Schedule a demo
          </Button>
        </div>
      </section>
    </div>
  );
}
